import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Groups,
  Schedule,
  CheckCircle,
  Cancel,
  Remove as RemoveIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const Batches = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [batches, setBatches] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    monthly_fee: 0,
    fee_due_day: 5,
    is_active: true,
    schedules: [{ day_of_week: 'Monday', start_time: '16:00', end_time: '17:00' }]
  });
  const [formErrors, setFormErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [batchStudents, setBatchStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedBatchForStudents, setSelectedBatchForStudents] = useState(null);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/api/admin/batches');
      setBatches(response.data.batches);
    } catch (error) {
      toast.error('Failed to load batches');
    }
  };

  const handleDialogOpen = (batch = null) => {
    if (batch) {
      setEditMode(true);
      setSelectedBatch(batch);
      setFormData({
        name: batch.name,
        description: batch.description || '',
        monthly_fee: batch.monthly_fee || 0,
        fee_due_day: batch.fee_due_day || 5,
        is_active: batch.is_active,
        schedules: batch.schedules && batch.schedules.length > 0 
          ? batch.schedules 
          : [{ day_of_week: 'Monday', start_time: '16:00', end_time: '17:00' }]
      });
    } else {
      setEditMode(false);
      setSelectedBatch(null);
      setFormData({
        name: '',
        description: '',
        monthly_fee: 0,
        fee_due_day: 5,
        is_active: true,
        schedules: [{ day_of_week: 'Monday', start_time: '16:00', end_time: '17:00' }]
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedBatch(null);
    setFormData({
      name: '',
      description: '',
      monthly_fee: 0,
      fee_due_day: 5,
      is_active: true,
      schedules: [{ day_of_week: 'Monday', start_time: '16:00', end_time: '17:00' }]
    });
    setFormErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;
    
    // Convert fee_due_day to integer
    if (name === 'fee_due_day') {
      processedValue = parseInt(value, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedules = [...formData.schedules];
    newSchedules[index][field] = value;
    setFormData(prev => ({ ...prev, schedules: newSchedules }));
    
    // Clear schedule errors
    if (formErrors.schedules) {
      setFormErrors(prev => ({ ...prev, schedules: '' }));
    }
  };

  const addSchedule = () => {
    setFormData(prev => ({
      ...prev,
      schedules: [...prev.schedules, { day_of_week: 'Monday', start_time: '16:00', end_time: '17:00' }]
    }));
  };

  const removeSchedule = (index) => {
    if (formData.schedules.length > 1) {
      const newSchedules = formData.schedules.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, schedules: newSchedules }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Batch name is required';
    }
    
    // Validate schedules
    if (!formData.schedules || formData.schedules.length === 0) {
      errors.schedules = 'At least one schedule is required';
    } else {
      // Check for duplicates
      const scheduleSet = new Set();
      for (const schedule of formData.schedules) {
        const key = `${schedule.day_of_week}-${schedule.start_time}-${schedule.end_time}`;
        if (scheduleSet.has(key)) {
          errors.schedules = `Duplicate schedule found: ${schedule.day_of_week} ${schedule.start_time}-${schedule.end_time}`;
          break;
        }
        scheduleSet.add(key);
        
        // Validate time order
        if (schedule.start_time >= schedule.end_time) {
          errors.schedules = 'Start time must be before end time';
          break;
        }
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`/api/admin/batches/${selectedBatch.id}`, formData);
        toast.success('Batch updated successfully');
      } else {
        await axios.post('/api/admin/batches', formData);
        toast.success('Batch created successfully');
      }
      handleDialogClose();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${editMode ? 'update' : 'create'} batch`);
    }
  };

  const handleDeleteClick = (batch) => {
    setBatchToDelete(batch);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/admin/batches/${batchToDelete.id}`);
      toast.success('Batch deleted successfully');
      setDeleteDialogOpen(false);
      setBatchToDelete(null);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete batch');
    }
  };

  const handleMenuOpen = (event, batch) => {
    setAnchorEl(event.currentTarget);
    setSelectedBatch(batch);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewStudents = async (batch) => {
    setSelectedBatchForStudents(batch);
    setStudentsDialogOpen(true);
    setLoadingStudents(true);
    try {
      const response = await axios.get(`/api/admin/students?batch_id=${batch.id}`);
      setBatchStudents(response.data.students || []);
    } catch (error) {
      toast.error('Failed to load students');
      setBatchStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleCloseStudentsDialog = () => {
    setStudentsDialogOpen(false);
    setSelectedBatchForStudents(null);
    setBatchStudents([]);
  };

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3 
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Batches
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage student batches and groups
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleDialogOpen()}
            fullWidth={isMobile}
          >
            Add Batch
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3, width: '100%', ml: 0 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Groups color="primary" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Total Batches
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {batches.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle color="success" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Active
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {batches.filter(b => b.is_active).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Cancel color="error" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Inactive
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {batches.filter(b => !b.is_active).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Groups color="info" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Total Students
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {batches.reduce((sum, b) => sum + b.student_count, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Batches Table */}
        <Card>
          <CardContent>
            {isMobile ? (
              /* Mobile Card View */
              <Stack spacing={2}>
                {batches.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No batches found. Click "Add Batch" to create one.
                  </Typography>
                ) : (
                  batches.map((batch) => (
                    <Card 
                      key={batch.id} 
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                        border: '1px solid',
                        borderColor: 'rgba(212, 175, 55, 0.2)',
                        boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                          transform: 'translateY(-2px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                        },
                      }}
                    >
                      <CardContent sx={{ pl: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416', fontSize: '1.1rem' }}>
                              {batch.name}
                            </Typography>
                            {batch.description && (
                              <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.8rem' }}>
                                {batch.description}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, batch)}
                            size="small"
                            sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                        
                        <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                        
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                              Schedule
                            </Typography>
                            {batch.schedules && batch.schedules.length > 0 ? (
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {batch.schedules.map((schedule, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Schedule fontSize="small" sx={{ color: '#D4AF37' }} />
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#2C2416' }}>
                                      {schedule.day_of_week}: {schedule.start_time} - {schedule.end_time}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#8B7355' }}>-</Typography>
                            )}
                          </Box>
                          
                          {batch.monthly_fee > 0 && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                                Monthly Fee
                              </Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: '#2C2416', mt: 0.5 }}>
                                ₹{parseFloat(batch.monthly_fee).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                <Typography component="span" variant="caption" sx={{ color: '#8B7355', ml: 1 }}>
                                  (Due: {batch.fee_due_day || 5}{batch.fee_due_day === 1 ? 'st' : batch.fee_due_day === 2 ? 'nd' : batch.fee_due_day === 3 ? 'rd' : 'th'})
                                </Typography>
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={`${batch.student_count} students`}
                              size="small"
                              sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)', fontWeight: 600 }}
                              variant="outlined"
                            />
                            {batch.is_active ? (
                              <Chip label="Active" color="success" size="small" />
                            ) : (
                              <Chip label="Inactive" color="error" size="small" />
                            )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Batch Name</strong></TableCell>
                      <TableCell><strong>Schedule</strong></TableCell>
                      <TableCell><strong>Monthly Fee</strong></TableCell>
                      <TableCell><strong>Students</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No batches found. Click "Add Batch" to create one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      batches.map((batch) => (
                        <TableRow key={batch.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {batch.name}
                            </Typography>
                            {batch.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {batch.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {batch.schedules && batch.schedules.length > 0 ? (
                              <Stack spacing={0.5}>
                                {batch.schedules.map((schedule, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Schedule fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      {schedule.day_of_week}: {schedule.start_time} - {schedule.end_time}
                                    </Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {batch.monthly_fee > 0 ? (
                            <Typography variant="body2" fontWeight={600}>
                              ₹{parseFloat(batch.monthly_fee).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${batch.student_count} students`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {batch.is_active ? (
                            <Chip label="Active" color="success" size="small" />
                          ) : (
                            <Chip label="Inactive" color="error" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, batch)}
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleViewStudents(selectedBatch);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Students</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleDialogOpen(selectedBatch);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDeleteClick(selectedBatch)}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Add/Edit Batch Dialog */}
        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editMode ? 'Edit Batch' : 'Add New Batch'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Batch Name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                    placeholder="e.g., Beginners Batch A"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the batch"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monthly Fee (₹)"
                    name="monthly_fee"
                    type="number"
                    value={formData.monthly_fee}
                    onChange={handleFormChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Monthly fee for students in this batch"
                    placeholder="e.g., 2500.00"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fee Due Day</InputLabel>
                    <Select
                      name="fee_due_day"
                      value={formData.fee_due_day}
                      onChange={handleFormChange}
                      label="Fee Due Day"
                    >
                      {[...Array(28)].map((_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of every month
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Day of month when fee payment is due. Rs. 100 penalty after this date.
                  </Typography>
                </Grid>
                
                {/* Schedule Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Class Schedule *
                    </Typography>
                    <Button
                      startIcon={<Add />}
                      onClick={addSchedule}
                      size="small"
                      variant="outlined"
                    >
                      Add Schedule
                    </Button>
                  </Box>
                  
                  {formErrors.schedules && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {formErrors.schedules}
                    </Alert>
                  )}
                  
                  <Stack spacing={2}>
                    {formData.schedules.map((schedule, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Day</InputLabel>
                                <Select
                                  value={schedule.day_of_week}
                                  onChange={(e) => handleScheduleChange(index, 'day_of_week', e.target.value)}
                                  label="Day"
                                >
                                  {daysOfWeek.map(day => (
                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={5} sm={3}>
                              <TextField
                                fullWidth
                                label="Start Time"
                                type="time"
                                value={schedule.start_time}
                                onChange={(e) => handleScheduleChange(index, 'start_time', e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={5} sm={3}>
                              <TextField
                                fullWidth
                                label="End Time"
                                type="time"
                                value={schedule.end_time}
                                onChange={(e) => handleScheduleChange(index, 'end_time', e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={2} sm={2}>
                              <IconButton
                                onClick={() => removeSchedule(index)}
                                disabled={formData.schedules.length === 1}
                                color="error"
                                size="small"
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editMode ? 'Update' : 'Create'} Batch
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Batch</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the batch <strong>"{batchToDelete?.name}"</strong>?
            </Typography>
            {batchToDelete?.student_count > 0 && (
              <Typography color="error" sx={{ mt: 2 }}>
                Warning: This batch has {batchToDelete.student_count} student(s) assigned.
                You cannot delete a batch with assigned students.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disabled={batchToDelete?.student_count > 0}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Students Dialog */}
        <Dialog 
          open={studentsDialogOpen} 
          onClose={handleCloseStudentsDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Groups color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Students in {selectedBatchForStudents?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {batchStudents.length} student{batchStudents.length !== 1 ? 's' : ''} enrolled
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {loadingStudents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : batchStudents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Groups sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Students Enrolled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This batch doesn't have any students yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Student Name</strong></TableCell>
                      <TableCell><strong>Parent Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchStudents.map((student) => (
                      <TableRow key={student.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {student.student_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {student.parent_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {student.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {student.parent_phone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {student.is_approved && student.is_active ? (
                            <Chip label="Active" color="success" size="small" />
                          ) : student.is_approved && !student.is_active ? (
                            <Chip label="Inactive" color="error" size="small" />
                          ) : (
                            <Chip label="Pending" color="warning" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseStudentsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default Batches;
